/* global fetch */

import { config } from '../config';
import {
  headers,
  stleaf,
  SpanTag,
  fetchAPIJSON,
  APIResponse,
} from '../helpers';

export interface OrganisationUser {
  id: string;
  scopeId: string;
  email: string;
  fname: string;
  lname: string;
}

/**
 * Simplified route for looking up users within organisation
 * @params search
 * @returns list of users
 */
export const getCurrentOrganisationUsers = (
  st: SpanTag,
  search: string,
): Promise<APIResponse<{ items: OrganisationUser[] }>> => {
  const url = new URL(`${config.API_URL}/v1/organisation/users/option`);
  const searchParams = new URLSearchParams({ search });
  url.search = searchParams.toString();
  return fetchAPIJSON(stleaf(st, 'cai5oo'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export interface GetUsersOptions {
  start?: number;
  limit?: number;
  search?: string;
  roleId?: string;
  workspaceId?: string;
  allSubWorkspaces?: 'yes' | 'no';
}

export const getUsers = (
  st: SpanTag,
  opts: GetUsersOptions,
): Promise<APIResponse<{ items: OrganisationUser[]; itemCount: number }>> => {
  const url = new URL(`${config.API_URL}/v2/organisation/user`);
  const searchParams = new URLSearchParams(opts as Record<string, string>);
  url.search = searchParams.toString();
  return fetchAPIJSON(stleaf(st, 'quac0e'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getMyDetails = (st: SpanTag) => {
  const url = `${config.API_URL}/v2/organisation/user/me`;
  return fetchAPIJSON(stleaf(st, 'iey6ai'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getUserDetails = (st: SpanTag, scopeId) => {
  const url = `${config.API_URL}/v2/organisation/user/${scopeId}`;
  return fetchAPIJSON(stleaf(st, 'roo4vo'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const create = (
  st: SpanTag,
  params: {
    fname: string;
    lname: string;
    email: string;
    organisationId?: string;
    roleId?: string;
    sendInvitation?: boolean;
    position: string;
    phone: string;
    location: any;
    workspaceId: string;
  },
) => {
  const url = `${config.API_URL}/v2/organisation/user`;
  return fetchAPIJSON(stleaf(st, 'meox5p'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const deleteUser = (st: SpanTag, id) => {
  const url = `${config.API_URL}/v1/organisation/users/${id}`;
  return fetchAPIJSON(stleaf(st, 'waequ0'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const deleteUserFromOrganisation = (st: SpanTag, userId) => {
  const url = `${config.API_URL}/v2/organisation/user/${userId}/all-workspaces`;
  return fetchAPIJSON(stleaf(st, 'thi3qu'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const deleteUserFromWorkspace = (st: SpanTag, userId, workspaceId) => {
  const url = `${config.API_URL}/v2/organisation/user/${userId}/workspace/${workspaceId}`;
  return fetchAPIJSON(stleaf(st, 'koh9im'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const resendInvitation = (
  st: SpanTag,
  userId: string,
  opts?: { workspaceId?: string },
) => {
  const url = `${config.API_URL}/v2/organisation/user/${userId}/resend-invitation`;
  return fetchAPIJSON(stleaf(st, 'au9ohj'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(opts ?? {}),
  });
};

export const changeRole = (st: SpanTag, userId, roleId, workspaceId) => {
  const url = `${config.API_URL}/v1/organisation/users/${userId}/role/${roleId}/workspace/${workspaceId}`;
  return fetchAPIJSON(stleaf(st, 'phahy6'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
  });
};

export const checkExisting = (
  st: SpanTag,
  opts: {
    email: string;
  },
) => {
  const url = `${config.API_URL}/v2/organisation/user/existing`;
  return fetchAPIJSON(stleaf(st, 'ohh9ai'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(opts),
  });
};

export const getOwners = (st: SpanTag, organisationId: string) => {
  const url = `${config.API_URL}/v1/organisation/${organisationId}/owners`;
  return fetchAPIJSON(stleaf(st, 'dfj34s'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/**
 * Invite multiple users to the organisation
 * Used in onboarding flow to invite team members
 */
export const inviteUsers = (
  st: SpanTag,
  params: {
    invites: Array<{
      email: string;
      roleId: string;
    }>;
    sendInviteEmail: boolean;
  },
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/organisation/user/invite-multiple`;
  return fetchAPIJSON(stleaf(st, 'au5doo'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};
